import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { DeleteIcon, DownloadIcon, EditIcon, EyeIcon } from "../../assets/Icons";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Tooltip from "@mui/material/Tooltip";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { ExpandQR } from "../modals/ExpandQR";
import moment from "moment/moment";
import { getInitials } from "../../utils/util";
import NoDataFound from "../NoDataFound";
import DeleteMachine from "../modals/DeleteMachine";

const AVATAR_PALETTE = [
  { bg: "#fde8e9", color: "#ca373c" },
  { bg: "#dbeafe", color: "#2563eb" },
  { bg: "#ede9fe", color: "#7c3aed" },
  { bg: "#dcfce7", color: "#16a34a" },
  { bg: "#fef3c7", color: "#d97706" },
];

const getAvatarStyle = (name = "") => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};

const SiteTablesV2 = ({ data, fetchAllMachines, page, pageSize, totalCount, onPageChange }) => {
  const userData = useSelector((state) => state.userSlice);
  const configData = useSelector((state) => state.configSlice);
  const [activeItems, setActiveItems] = useState({});
  const [showQR, setShowQR] = useState({ machineName: null, qr: null, active: false });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteMachineId, setDeleteMachineId] = useState("");
  const canManage = userData?.user?.permissions?.includes("MACHINE.ADD");

  const handleSwitch = async (uuid) => {
    const newState = !activeItems[uuid];
    try {
      await axios.put(
        `${configData.config.API_URL}/api/machine/update/${uuid}`,
        { isActive: newState },
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
            "Content-Type": "application/json",
          },
        },
      );
      setActiveItems((prev) => ({ ...prev, [uuid]: newState }));
    } catch (error) {
      console.error("Failed to update visibility", error);
    } finally {
      fetchAllMachines();
    }
  };

  const onDeleteClick = (uuid) => {
    setShowDeleteModal(true);
    setDeleteMachineId(uuid);
  };

  const deleteHandler = async () => {
    try {
      if (deleteMachineId) {
        await axios.put(
          `${configData.config.API_URL}/api/machine/delete/${deleteMachineId}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${userData.token}`,
              "Content-Type": "application/json",
            },
          },
        );
      }
    } catch (error) {
      console.log(`Error in deleting machine ${error}`);
    } finally {
      setShowDeleteModal(false);
      fetchAllMachines();
    }
  };

  const handleDownloadQr = useCallback((qrCode) => {
    const link = document.createElement("a");
    link.href = qrCode;
    link.download = "QRCode.png";
    link.click();
  }, []);

  useEffect(() => {
    const initialActiveItems = data.reduce((acc, item) => {
      acc[item.uuid] = item.isActive;
      return acc;
    }, {});
    setActiveItems(initialActiveItems);
  }, [data]);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const startIndex = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const endIndex = Math.min(page * pageSize, totalCount);

  return (
    <div className="sites-v2__table-wrap">
      <div className="table-responsive sites-v2__table-scroll">
        <table className="table sites-v2__table mb-0">
          <thead>
            <tr>
              <th>S.No</th>
              <th>Site</th>
              <th>Created At</th>
              <th>Updated At</th>
              <th>Site ID</th>
              <th>Tags</th>
              {canManage && <th>Visibility</th>}
              <th>Actions</th>
              <th>QR Code</th>
            </tr>
          </thead>
          <tbody>
            {data.length > 0 ? (
              data.map((item, index) => {
                const avatar = getAvatarStyle(item.name);
                return (
                <tr key={item.uuid}>
                  <td>{(page - 1) * pageSize + index + 1}</td>
                  <td>
                    <Link to={`/site-info/${item.uuid}`} className="sites-v2__site-link">
                      {item.thumbnail ? (
                        <img className="sites-v2__site-thumb" src={item.thumbnail} alt="" />
                      ) : (
                        <span
                          className="sites-v2__site-initials"
                          style={{
                            backgroundColor: avatar.bg,
                            color: avatar.color,
                          }}
                        >
                          {getInitials(item.name)}
                        </span>
                      )}
                      <span className="sites-v2__site-text">
                        <strong>{item.name}</strong>
                        <small>ID: {item.data?.machineId || "—"}</small>
                      </span>
                    </Link>
                  </td>
                  <td className="sites-v2__date-cell">
                    <div className="sites-v2__date-row">
                      <EventOutlinedIcon className="sites-v2__date-icon" />
                      <span>{moment(item.createdAt).format("MMM D, YYYY")}</span>
                    </div>
                    <small>{moment(item.createdAt).format("h:mm A")}</small>
                  </td>
                  <td className="sites-v2__date-cell">
                    <div className="sites-v2__date-row">
                      <EventOutlinedIcon className="sites-v2__date-icon" />
                      <span>{moment(item.updatedAt).format("MMM D, YYYY")}</span>
                    </div>
                    <small>{moment(item.updatedAt).format("h:mm A")}</small>
                  </td>
                  <td>{item.data?.machineId || "—"}</td>
                  <td>
                    <div className="sites-v2__tags">
                      {item.tags?.length
                        ? item.tags.map((tag) => (
                            <span key={tag.id} className="sites-v2__tag">
                              {tag.name}
                            </span>
                          ))
                        : "—"}
                    </div>
                  </td>
                  {canManage && (
                    <td>
                      <div className="checkbox-apple">
                        <input
                          onChange={() => handleSwitch(item.uuid)}
                          className="yep"
                          id={`v2-check-${item.uuid}`}
                          type="checkbox"
                          checked={!!activeItems[item.uuid]}
                        />
                        <label htmlFor={`v2-check-${item.uuid}`}></label>
                      </div>
                    </td>
                  )}
                  <td>
                    <div className="sites-v2__actions">
                      <Tooltip title="Edit site">
                        <Link to={`/site-info/${item.uuid}`} className="sites-v2__action-btn">
                          <EditIcon />
                        </Link>
                      </Tooltip>
                      <Tooltip
                        title={
                          userData?.user?.uuid === item?.owner?.uuid
                            ? "Delete site"
                            : "Contact the site creator to delete"
                        }
                      >
                        <button
                          type="button"
                          className={`sites-v2__action-btn sites-v2__action-btn--danger ${
                            userData?.user?.uuid === item?.owner?.uuid ? "" : "disabled"
                          }`}
                          onClick={() => {
                            if (userData?.user?.uuid === item?.owner?.uuid) onDeleteClick(item.uuid);
                          }}
                        >
                          <DeleteIcon />
                        </button>
                      </Tooltip>
                      <Tooltip title="More options">
                        <Link to={`/site-info/${item.uuid}`} className="sites-v2__action-btn">
                          <MoreVertIcon className="sites-v2__more-icon" />
                        </Link>
                      </Tooltip>
                    </div>
                  </td>
                  <td>
                    <div className="sites-v2__qr">
                      <img src={item.qr} alt="QR Code" />
                      <div className="sites-v2__qr-overlay">
                        <Tooltip title="View">
                          <button
                            type="button"
                            className="sites-v2__qr-btn"
                            onClick={() =>
                              setShowQR({ active: true, qr: item.qr, machineName: item.name })
                            }
                          >
                            <EyeIcon />
                          </button>
                        </Tooltip>
                        <Tooltip title="Download">
                          <button
                            type="button"
                            className="sites-v2__qr-btn"
                            onClick={() => handleDownloadQr(item.qr)}
                          >
                            <DownloadIcon />
                          </button>
                        </Tooltip>
                      </div>
                    </div>
                  </td>
                </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={canManage ? 9 : 8}>
                  <NoDataFound
                    nodatatext="No Results Found"
                    dataInfo="Try adjusting your search or filters"
                  />
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="sites-v2__pagination">
        <p>
          Showing {startIndex} to {endIndex} of {totalCount} sites
        </p>
        <div className="sites-v2__pagination-controls">
          <button type="button" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
            ‹
          </button>
          <span className="active">{page}</span>
          <button type="button" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
            ›
          </button>
        </div>
      </div>

      {showQR.active && <ExpandQR showQR={showQR} setShowQR={setShowQR} />}
      {showDeleteModal && (
        <DeleteMachine
          showDeleteModal={showDeleteModal}
          onModalCancel={() => {
            setShowDeleteModal(false);
            setDeleteMachineId("");
          }}
          onDeleteClick={deleteHandler}
        />
      )}
    </div>
  );
};

export default SiteTablesV2;
